import { Bell, Settings, Video, Wallet } from "lucide-react";

// Define props interface for the notification card
interface AdminNotificationCardProps {
  notification: any;
  onMarkAsRead: (id: string) => void;
}

/**
 * AdminNotificationCard component
 * Displays a single notification card with appropriate styling based on notification type
 */
export function AdminNotificationCard({
  notification,
  onMarkAsRead,
}: AdminNotificationCardProps) {
  return (
    <div
      key={notification.id}
                    onClick={() => onMarkAsRead(notification.id)}

      className={`p-4 border cursor-pointer rounded-lg ${
        notification.read ? "bg-white" : "bg-[#F15D180A]"
      } hover:bg-gray-50 transition-colors`}
    >
      <div className="flex items-start">
        {/* Display appropriate icon based on notification type */}
        {notification.trigger === "fund_request" ||
        notification.trigger === "fund_request_receive" ||
        notification.trigger === "fund_request_send" ||
        notification.trigger === "fund_request_approved" ||
        notification.trigger === "fund_request_rejected" ||
        notification.trigger === "funds_distributed" ||
        notification.trigger === "funds_received" ||
        notification.trigger === "funds_added" ? (
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3">
            <Wallet className="w-5 h-5 text-amber-600" />
          </div>
        ) : notification.trigger === "new_support_request" ||
          notification.trigger === "support_request_accepted" ||
          notification.trigger === "support_request_submitted" ||
          notification.trigger === "support_request_rejected" ||
          notification.trigger === "support_request_missed" ? (
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3">
            <Video className="w-5 h-5 text-blue-600" />
          </div>
        ) : notification.trigger === "system_notification" ||
          notification.trigger === "admin_notification" ? (
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3">
            <Settings className="w-5 h-5 text-purple-600" />
          </div>
        ) : (
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3">
            <Bell className="w-5 h-5 text-gray-600" />
          </div>
        )}

        <div className="flex-1">
          {/* Notification Category Tag */}
          <div className="flex justify-between items-center mb-1">
            { notification.trigger === "fund_request" ||
            notification.trigger === "fund_request_receive" ||
            notification.trigger === "fund_request_send" ||
            notification.trigger === "fund_request_approved" ||
            notification.trigger === "fund_request_rejected" ||
            notification.trigger === "funds_received" ||
            notification.trigger === "funds_added" ||
            notification.trigger === "funds_distributed" ? (
              <span className="px-2 py-0.5 text-xs bg-amber-200 text-amber-800 rounded-full inline-block">
                Fund Request
              </span>
            ) : notification.trigger === "new_support_request" ||
              notification.trigger === "support_request_submitted" ||
              notification.trigger === "support_request_accepted" ||
              notification.trigger === "support_request_rejected" ||
              notification.trigger === "support_request_missed" ? (
              <span className="px-2 py-0.5 text-xs bg-blue-200 text-blue-800 rounded-full inline-block">
                Support Request
              </span>
            ) : notification.trigger === "system_notification" ||
              notification.trigger === "admin_notification" ? (
              <span className="px-2 py-0.5 text-xs bg-purple-200 text-purple-800 rounded-full inline-block">
                System
              </span>
            ) : (
              <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-800 rounded-full inline-block">
                Notification
              </span>
            )}

            {/* User info badge for admin view */}
            {notification.userEmail && (
              <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-800 rounded-full inline-block ml-2">
                {notification.userEmail}
              </span>
            )}

            {!notification.read && (
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
            )}
          </div>

          {/* Notification Message */}
          <p className="text-sm font-medium text-gray-800 mb-1">
            {notification.message}
          </p>

          {/* Additional details for support requests */}
          {notification.trigger === "new_support_request" &&
            notification.meta?.question && (
              <p className="text-xs text-blue-600 mb-1">
                Question: {notification.meta.question.substring(0, 50)}
                {notification.meta.question.length > 50 ? "..." : ""}
              </p>
            )}
          {notification.trigger === "new_support_request" &&
            notification.meta?.customerName && (
              <p className="text-xs text-blue-600 mb-1">
                Requested by: {notification.meta.customerName || "Unknown"}
              </p>
            )}

          {/* Additional details for fund requests */}
          {(notification.trigger === "fund_request_details" ||
            notification.trigger === "fund_request_receive") && (
            <p className="text-xs text-amber-600 mb-1">
              Requested by:{" "}
              {notification.meta?.requesterName ||
                notification.userEmail ||
                "Unknown"}
            </p>
          )}
          {notification.trigger === "fund_request_send" && (
            <p className="text-xs text-blue-600 mb-1">
              Status: {notification.meta?.status || "pending"}
            </p>
          )}

          {/* Admin metadata display */}
          {notification.meta && (
            <div className="bg-gray-50 w-fit p-2 rounded text-xs text-gray-600 mt-2 mb-1">
              <p>
                <span className="font-semibold">Trigger:</span>{" "}
                {notification.trigger}
                {notification.triggeredBy && (
                  <span>
                    {" "}
                    | <span className="font-semibold">By:</span>{" "}
                    {notification.triggeredBy}
                  </span>
                )}
                {notification.meta.status && (
                  <span>
                    {" "}
                    | <span className="font-semibold">Status:</span>{" "}
                    {notification.meta.status}
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Timestamp */}
          <p className="text-xs text-gray-500">
            {notification.createdAt
              ? new Date(notification.createdAt).toLocaleString()
              : "Just now"}
          </p>

        
        </div>
      </div>
    </div>
  );
}
